/**
 * Reel Video Adapter
 * Implements IFullVideoGeneratorAdapter for reel video generation
 */

import { useMemo } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import type {
  IFullVideoGeneratorAdapter,
  IVideoForConcatenation,
  ICreateFinalVideoParams,
} from '@/@types/fullVideoGenerator';

interface IMuxedVideoItem {
  _id: Id<'sceneVideos'>;
  sceneId: Id<'reelScenes'>;
  sceneNumber: number;
  videoUrl: string | null;
  muxedVideoUrl: string | null;
  muxedStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  captionedVideoUrl: string | null;
  captionedStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  sceneType: string;
  voiceoverDurationSeconds?: number;
}

/**
 * Hook to create a reel video adapter
 * Uses Convex hooks internally for reactivity
 */
export function useReelVideoAdapter(
  reelId: Id<'reels'>,
  totalSceneCount: number
): {
  adapter: IFullVideoGeneratorAdapter;
  muxedVideos: IMuxedVideoItem[] | undefined;
  isReady: boolean;
  readyMessage: string;
} {
  const muxedVideos = useQuery(api.reelVideos.getMuxedVideosForReel, { reelId });
  const generateUploadUrlMutation = useMutation(api.reelVideos.generateUploadUrl);
  const createReelVideoMutation = useMutation(api.reelVideos.createReelVideo);

  // Calculate readiness — compare against total scene count, not just returned videos
  const { isReady, readyMessage } = useMemo(() => {
    if (!muxedVideos) {
      return { isReady: false, readyMessage: 'Loading...' };
    }

    const ready = muxedVideos.filter((v: IMuxedVideoItem) => {
      // A scene is ready if it has either a completed muxed video OR a direct video URL.
      // Muxed video takes priority (non-avatar scenes go through a mux step).
      // Scenes without mux (avatar_only, split-screen) use the direct video URL.
      if (v.muxedVideoUrl !== null && v.muxedStatus === 'completed') return true;
      return v.videoUrl !== null;
    });

    if (totalSceneCount === 0) {
      return { isReady: false, readyMessage: 'No scenes with videos' };
    }

    if (ready.length < totalSceneCount) {
      const missing = totalSceneCount - ready.length;
      return {
        isReady: false,
        readyMessage: `${missing} scene(s) need video generation`,
      };
    }

    // Check if any scenes have captions still processing — wait before assembly
    const captionsInProgress = muxedVideos.filter(
      (v: IMuxedVideoItem) => v.captionedStatus === 'processing' || v.captionedStatus === 'pending'
    );
    if (captionsInProgress.length > 0) {
      return {
        isReady: false,
        readyMessage: `Burning captions on ${captionsInProgress.length} scene(s)...`,
      };
    }

    return { isReady: true, readyMessage: '' };
  }, [muxedVideos, totalSceneCount]);

  const adapter: IFullVideoGeneratorAdapter = useMemo(
    () => ({
      videoType: 'reel',
      targetResolution: { width: 1080, height: 1920 },

      getVideosForConcatenation: async (): Promise<IVideoForConcatenation[]> => {
        if (!muxedVideos) {
          throw new Error('No videos available');
        }

        const videos = muxedVideos.filter((v: IMuxedVideoItem) => {
          if (v.muxedVideoUrl !== null && v.muxedStatus === 'completed') return true;
          return v.videoUrl !== null;
        });

        return videos.map((v: IMuxedVideoItem) => ({
          id: v._id,
          sourceId: v.sceneId,
          order: v.sceneNumber,
          // 3-tier priority: captioned (captions burned in) > muxed (audio layered) > raw
          videoUrl:
            v.captionedVideoUrl && v.captionedStatus === 'completed'
              ? v.captionedVideoUrl
              : v.muxedVideoUrl && v.muxedStatus === 'completed'
                ? v.muxedVideoUrl
                : v.videoUrl!,
          voiceoverDurationSeconds: v.voiceoverDurationSeconds ?? 5,
        }));
      },

      generateUploadUrl: async (): Promise<string> => {
        return await generateUploadUrlMutation();
      },

      createFinalVideoRecord: async (params: ICreateFinalVideoParams): Promise<string> => {
        const reelVideoId = await createReelVideoMutation({
          reelId,
          videoStorageId: params.storageId,
          durationSeconds: params.durationSeconds,
          fileSizeBytes: params.fileSizeBytes,
          resolution: '1080x1920',
          sceneVideoIds: params.sourceVideoIds as Id<'sceneVideos'>[],
        });
        return reelVideoId;
      },
    }),
    [muxedVideos, generateUploadUrlMutation, createReelVideoMutation, reelId]
  );

  return {
    adapter,
    muxedVideos,
    isReady,
    readyMessage,
  };
}
